import { useState, useEffect } from "react";
import Input from "../../../../../components/fields/input";
import Button from "../../../../../components/buttons/button";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
} from "../../../../../store/Reducer/events";
import toast from "react-hot-toast";
import { withHttps } from "../../../../../utils/links";
import { useCreateFileMutation } from "../../../../../store/Reducer/file";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { hasChanges } from "./event-modal-helper";
import MiniLoader from "../../../../../components/loaders/mini-loader";

export default function CreateEventsModal({
  isOpen,
  closeModal,
  isEdit,
  rowData,
}) {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState("One-Off");
  const [createEvent, { isLoading: eventLoading }] = useCreateEventMutation();
  const [createFile, { isLoading: fileLoading }] = useCreateFileMutation();
  const [updateEvent, { isLoading: eventLoadingUpdate }] =
    useUpdateEventMutation();
  console.log("rowData", rowData);

  const [formData, setFormData] = useState({
    type: selectedType,
    title: "",
    details: "",
    venue: "",
    time: "",
    date: "",
    registrationLink: "",
    socialGroupLink: "",
    file: null,
    kidsWelcome: false,
    days: [],
    isActive: true,
    isPublished: false,
  });

  const [filePreview, setFilePreview] = useState(null);

  const resetForm = () => {
    setFormData({
      type: selectedType,
      title: "",
      details: "",
      venue: "",
      time: "",
      date: "",
      registrationLink: "",
      socialGroupLink: "",
      file: null,
      kidsWelcome: false,
      days: [],
      isActive: true,
      isPublished: false,
    });
    setFilePreview(null);
  };

  // Add effect to reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Update existing effect to only set form data when modal opens for editing
  useEffect(() => {
    if (isOpen && isEdit && rowData) {
      setFormData({
        type: rowData?.type,
        title: rowData.title || "",
        details: rowData.details || "",
        venue: rowData.venue || "",
        time: rowData.time || "",
        date: rowData.date ? moment(rowData.date).format("YYYY-MM-DD") : "",
        registrationLink: rowData.registrationLink || "",
        socialGroupLink: rowData.socialGroupLink || "",
        file: rowData?.file,
        kidsWelcome: rowData.kidsWelcome || false,
        days: rowData.recurringDays || [],
        isActive: rowData.isActive,
        isPublished: rowData.isPublished,
      });
      setSelectedType(rowData?.type)
    }
  }, [isOpen, isEdit, rowData, selectedType]);

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handleDailyChange = (e) => {
    const isChecked = e.target.checked;
    handleChange({
      target: {
        name: "days",
        value: isChecked ? daysOfWeek : [],
      },
    });
  };

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;

    // Handle file input separately to create an image preview
    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));

      // Generate the preview URL for the selected file
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setFilePreview(previewUrl); // Store the preview URL
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };



  useEffect(() => {
    // If editing, set the filePreview to the existing file URL from rowData
    if (isEdit && rowData?.file?.url) {
      setFilePreview(rowData?.file?.url);
    }
  }, [isEdit, rowData]);

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEdit && !hasChanges(formData, rowData)) {
      toast("No changes detected");
      // closeModal();
      return;
    }
    if (selectedType === "One-Off" && formData.date) {
      const selectedDate = moment(formData.date);
      const today = moment().startOf("day");
      if (selectedDate.isBefore(today)) {
        toast.error("Date cannot be in the past. Please select a valid date.");
        return;
      }
    }

    let fileData = null;

    // Prepare event data as a JSON object
    const eventData = {
      type: selectedType,
      title: formData.title,
      details: formData.details,
      venue: formData.venue,
      time: formData.time,
      registrationLink: withHttps(formData.registrationLink),
      socialGroupLink: withHttps(formData.socialGroupLink),
      kidsWelcome: formData.kidsWelcome,
      recurringDays: formData.days || [],
      // Publishing decides what shows; there is no separate on/off.
      isActive: true,
      isPublished: formData.isPublished,
    };

    if (isEdit && rowData?._id) {
      eventData.id = rowData._id; // Include the ID if editing an existing event
    }

    if (selectedType === "One-Off") {
      eventData.date = formData.date;
    }

    // Upload the image if it exists
    if (formData.file && formData.file instanceof File) {
      try {
        // Upload the image separately
        const imageResponse = await createFile(formData.file).unwrap();
        fileData = {
          name: imageResponse?.data?.fileName || formData.file.name,
          url: imageResponse?.data?.fileUrl || formData.file.name, // Assuming fileResponse contains `url`
        };
      } catch (error) {
        // Display the error toast if image upload fails
        toast.error(
          "Image upload failed: " +
          (error?.data?.message || error.message || "Unknown error")
        );
        return;
      }
    }

    // If image upload is successful, add the image URL to the event data
    if (fileData) {
      eventData.file = fileData ? fileData : undefined;
    }

    try {
      // Submit event data as a JSON body
      let result;
      if (isEdit) {
        // Call updateEvent if editing
        result = await updateEvent(eventData).unwrap();
        toast.success("Event updated successfully!");
      } else {
        // Call createEvent if creating
        console.log('event payload', eventData)
        result = await createEvent(eventData).unwrap();
        toast.success("Event created successfully!");
      }

      closeModal();
    } catch (error) {
      // Improved error handling
      const errorMessage =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "An error occurred. Please try again.";

      console.error("Error submitting form:", error);
      toast.error(errorMessage); // Display the error message from the server in the toast
    }
  };

  console.log("isPublished", formData?.isPublished);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-0" onClick={closeModal}>
          <div className="rounded-lg shadow-xl w-full max-w-3xl mx-auto h-full sm:h-auto sm:max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-full bg-white rounded-lg shadow-lg p-4 sm:px-6 sm:py-6">
              <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                {rowData ? t("events_edit_event_modal_title") : t("events_create_event_modal_title")}
              </h1>

              {fileLoading || eventLoading || (eventLoadingUpdate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="loader border-t-4 border-blue-500 rounded-full w-16 h-16 animate-spin"></div>
                  <p className="text-white mt-4">Loading...</p>
                </div>
              ))}


              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Event Type */}
                <div className="flex items-center space-x-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="eventType"
                      value="One-Off"
                      checked={selectedType === "One-Off"}
                      onChange={handleTypeChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2">One-Off</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="eventType"
                      value="Recurring"
                      checked={selectedType === "Recurring"}
                      onChange={handleTypeChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2">Recurring</span>
                  </label>
                </div>

                {filePreview && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700">
                      Image Preview
                    </label>
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="w-24 h-24 mt-2 rounded"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Event Title */}
                  <Input
                    label="Event Title"
                    type="text"
                    name="title"
                    placeholder="Enter Event Title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                  {/* Venue */}
                  <Input
                    label="Venue"
                    type="text"
                    name="venue"
                    placeholder="Enter Venue"
                    value={formData.venue}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <textarea
                    id="details"
                    name="details"
                    rows="4"
                    className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none placeholder:text-gray-500"
                    placeholder="Enter event details..."
                    value={formData.details}
                    onChange={handleChange}
                  ></textarea>
                </div>

                {selectedType === "Recurring" && (
                  <div className="w-full">
                    <label className="block text-md font-medium text-gray-700">
                      Days
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="daily"
                          checked={formData.days.length === daysOfWeek.length}
                          onChange={handleDailyChange}
                          className="h-4 w-4 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span>Daily</span>
                      </label>
                      {daysOfWeek.map((day) => (
                        <label
                          key={day}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            name={`day-${day}`}
                            checked={formData.days?.includes(day)}
                            onChange={(e) => {
                              const newDays = e.target.checked
                                ? [...(formData.days || []), day]
                                : formData.days.filter((d) => d !== day);
                              handleChange({
                                target: { name: "days", value: newDays },
                              });
                            }}
                            className="h-4 w-4 rounded border-gray-300 focus:ring-blue-500"
                          // required
                          />
                          <span>{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Time and Date */}

                  {selectedType === "One-Off" && (
                    <div>
                      <div className="relative max-w-sm">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none">
                          <svg
                            className="w-4 h-4 text-gray-500 dark:text-gray-400"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                          </svg>
                        </div>
                        <input
                          type="date"
                          name="date"
                          value={formData.date || ""}
                          onChange={handleChange}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-3 focus:outline-none placeholder:text-gray-500"
                          placeholder="Select date"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <Input
                    label="Time"
                    type="time"
                    name="time"
                    placeholder="Enter Time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                  />

                  {/* Registration Link */}
                  <Input
                    label="Registration Link (optional)"
                    type="text"
                    inputMode="url"
                    name="registrationLink"
                    placeholder="e.g. forms.google.com/…"
                    value={formData.registrationLink}
                    onChange={handleChange}
                    onBlur={(e) => setFormData((prev) => ({ ...prev, registrationLink: withHttps(e.target.value) }))}
                  />

                  {/* Social Group Link */}
                  <Input
                    label="Social Group Link (optional)"
                    type="text"
                    inputMode="url"
                    name="socialGroupLink"
                    placeholder="e.g. forms.google.com/…"
                    value={formData.socialGroupLink}
                    onChange={handleChange}
                    onBlur={(e) => setFormData((prev) => ({ ...prev, socialGroupLink: withHttps(e.target.value) }))}
                  />
                </div>

                {/* Upload Image */}
                <div className="w-full">
                  <label htmlFor="image" className="sr-only">
                    Choose file
                  </label>
                  <input
                    type="file"
                    name="file"
                    id="image"
                    accept="image/*"
                    onChange={handleChange}
                    className="block w-full border border-gray-300 shadow-sm rounded-lg text-sm focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 file:bg-gray-50 file:border-0 file:me-4 file:py-3 file:px-4"
                    required={!isEdit || !formData.file}
                  />
                </div>

                {/* Kids Welcome */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="kidsWelcome"
                    id="kidsWelcome"
                    checked={formData.kidsWelcome}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  // required
                  />
                  <label
                    htmlFor="kidsWelcome"
                    className="ml-2 text-sm text-gray-700 cursor-pointer"
                  >
                    Kids Welcome
                  </label>
                </div>


                {/* Action Buttons */}
                <div className="flex justify-end items-center space-x-3 p-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg font-semibold text-sm border border-gray-300 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring focus:ring-gray-200"
                  >
                    {t("universal_cancel_text")}
                  </button>

                  <Button
                    text={t("universal_save_text")
                    }
                    type="submit"
                    className="px-4 py-2 rounded-lg font-semibold text-sm bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring focus:ring-blue-300"
                  />

                  <div className="flex items-center space-x-3">
                    


                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="isPublished"
                        id="isPublished"
                        checked={formData.isPublished}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, isPublished: e.target.checked }))
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor="isPublished"
                        className="ml-2 text-sm text-gray-700 cursor-pointer"
                      >
                        Publish on your website
                      </label>
                    </div>
                  </div>

                </div>

              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
